import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService, ApiRequestOptions } from '../../../../core/services/api.service';
import { apiUrlFor } from '../../../../core/services/prism-new-routing';
import { CallAuditActivity, ReviewTimePayload, TrackEventItem } from '../models/call-audit-activity.model';

/** JSON.stringify replacer: prism-new's optional botId / metadata / productName are not nullable. */
const dropNulls = (_key: string, value: unknown) => (value === null ? undefined : value);

/**
 * Silent telemetry client for call-audit user events and QA active-time.
 * Mirrors `core/services/audit.service.ts`: all writes are fire-and-forget with
 * loader/toaster suppressed so tracking never disrupts the reviewer.
 */
@Injectable({ providedIn: 'root' })
export class CallAuditTrackingService {
  private api = inject(ApiService);

  private readonly silent: ApiRequestOptions = {
    showLoader: false,
    showErrorToaster: false,
  };

  /** Persist a batch of page-level user events. */
  trackEvents(events: TrackEventItem[]): Observable<any> {
    return this.api.post('/call-audit/events', { events }, this.silent);
  }

  /** Accumulate a slice of active review time. */
  reviewTime(payload: ReviewTimePayload): Observable<any> {
    return this.api.post('/call-audit/review-time', payload, this.silent);
  }

  /** Fetch the activity timeline + per-reviewer active time for one audit. */
  getAuditActivity(auditId: string): Observable<CallAuditActivity> {
    return this.api.get<CallAuditActivity>(`/call-audit/${auditId}/activity`, {}, this.silent);
  }

  /**
   * Reliable review-time flush for page unload, using native fetch with keepalive
   * (standard HttpClient is aborted on unload). Copies the pattern from
   * `whitelabel-chat.service.ts:saveAssistantIframeConfigSync`.
   */
  reviewTimeBeacon(payload: ReviewTimePayload): void {
    try {
      const url = apiUrlFor('POST', 'call-audit/review-time');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      const token = sessionStorage.getItem('access_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const csrf = this.readCookie('prism-auth_csrf_token');
      if (csrf) headers['X-CSRF-Token'] = csrf;

      void fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload, dropNulls),
        keepalive: true,
        credentials: 'include',
      }).catch(() => void 0);
    } catch {
      // Best-effort on unload; ignore failures.
    }
  }

  /** Batch-events flush for page unload via keepalive fetch. */
  trackEventsBeacon(events: TrackEventItem[]): void {
    if (!events || events.length === 0) return;
    try {
      const url = apiUrlFor('POST', 'call-audit/events');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      const token = sessionStorage.getItem('access_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const csrf = this.readCookie('prism-auth_csrf_token');
      if (csrf) headers['X-CSRF-Token'] = csrf;

      void fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ events }, dropNulls),
        keepalive: true,
        credentials: 'include',
      }).catch(() => void 0);
    } catch {
      // Best-effort on unload; ignore failures.
    }
  }

  private readCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }
}
